import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PictureInPicture, MessageSquare, Layers, ExternalLink, Image as ImageIcon, Edit3 } from 'lucide-react';

const adFormats = [
  {
    title: 'Pop-up Ads',
    description: 'High-impact overlay ads that grab user attention immediately. Optimized for conversions.',
    icon: <ExternalLink className="h-12 w-12 text-neon-pink mb-4" />,
    color: 'neon-pink',
  },
  {
    title: 'Push Notifications',
    description: 'Engage users directly on their devices with timely and relevant push messages.',
    icon: <MessageSquare className="h-12 w-12 text-neon-blue mb-4" />,
    color: 'neon-blue',
  },
  {
    title: 'In-Page Push',
    description: 'Native-looking push messages that appear within the website content for seamless integration.',
    icon: <Layers className="h-12 w-12 text-neon-purple mb-4" />,
    color: 'neon-purple',
  },
  {
    title: 'Native Ads',
    description: 'Ads that blend seamlessly with the surrounding content for a non-disruptive user experience.',
    icon: <PictureInPicture className="h-12 w-12 text-neon-pink mb-4" />,
    color: 'neon-pink',
  },
  {
    title: 'Banner Ads',
    description: 'Classic display ads in various sizes, strategically placed for maximum visibility.',
    icon: <ImageIcon className="h-12 w-12 text-neon-blue mb-4" />,
    color: 'neon-blue',
  },
  {
    title: 'Custom Ad Formats',
    description: 'Tailor-made ad solutions designed to meet your unique campaign goals and brand identity.',
    icon: <Edit3 className="h-12 w-12 text-neon-purple mb-4" />,
    color: 'neon-purple',
  }
];

const AdFormatsSection = () => {
  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.9 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        delay: i * 0.15,
        duration: 0.5,
        ease: 'easeOut',
      },
    }),
    hover: {
      y: -10,
      scale: 1.03,
      boxShadow: "0 0 25px var(--hover-color), 0 0 10px var(--hover-color) inset",
      transition: { duration: 0.3, ease: 'easeOut' }
    }
  };

  return (
    <section id="ad-formats" className="py-16 md:py-24">
      <div className="container mx-auto px-4">
        <motion.h2 
          className="text-3xl md:text-4xl font-bold text-center mb-4 text-glow-purple animated-text-glow"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5 }}
        >
          Diverse Ad Formats
        </motion.h2>
        <motion.p 
          className="text-lg text-cyber-text-secondary text-center mb-12 md:mb-16 max-w-2xl mx-auto animated-text-glow"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          High-impact formats engineered to capture attention and drive conversions
        </motion.p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {adFormats.map((format, index) => (
            <motion.div
              key={format.title}
              custom={{ '--hover-color': `var(--${format.color})` }}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              whileHover="hover"
              viewport={{ once: true, amount: 0.2 }}
            >
              <Card className={`glassmorphism h-full flex flex-col border border-${format.color}/50 transition-all duration-300`}>
                <CardHeader className="items-center text-center">
                  {React.cloneElement(format.icon, { className: `${format.icon.props.className} group-hover:scale-110 transition-transform` })}
                  <CardTitle className={`text-2xl font-semibold text-glow-${format.color} animated-text-glow`}>{format.title}</CardTitle>
                </CardHeader>
                <CardContent className="text-center flex-grow">
                  <p className="text-cyber-text-secondary animated-text-glow">{format.description}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AdFormatsSection;